/**
 * Shared IX3 Transform Logic
 *
 * This module contains pure transform functions that are reused across different
 * IX3 transformation contexts (publishing, stylesheet generation, etc.).
 */

import type {ClassNameMap, StyleBlockId} from '@packages/systems/style/types';
import type {VariableAction} from '@packages/systems/ix3-wf-plugins/shared';
import {
  canonicalizeCustomPropertyName,
  isCustomPropertyAllowed,
} from '@packages/systems/ix3/schema';
import {weakMemo} from '@packages/utilities/memo';
import {getNameFromClassNameMap} from '@packages/systems/style-legacy/utils';
import {cssifyName} from '@packages/systems/core/utils/TextUtils';

/**
 * Transforms a class target from style block IDs to CSS-safe class names.
 * Returns the transformed value to be assigned to data[1].
 *
 * This is used by both:
 * - Publishing transforms (getIx3Js)
 * - Stylesheet transforms (applyIX3Transforms)
 *
 * @param targetValue - The target value (second element of TargetDefinition)
 * @param classNameMap - Map of style block IDs to class names
 * @returns Transformed target value, or undefined if no transformation needed
 */
export function transformClassTargetValue(
  targetValue: unknown,
  classNameMap: ClassNameMap
): unknown | undefined {
  if (!Array.isArray(targetValue)) return;

  const hasScope = Array.isArray(targetValue[0]);
  const styleBlockIds = hasScope ? targetValue[0] : targetValue;
  const scope = hasScope ? targetValue[1] : undefined;

  if (styleBlockIds.length === 0) return;
  const getNameById = getClassName(classNameMap);

  const result = styleBlockIds
    .flatMap((guid: string) => getNameById(guid) || [])
    .join('.');
  return scope ? [result, scope] : [result];
}

/**
 * Transforms class action selectors from style block IDs to CSS-safe class names.
 * Returns the transformed selectors array.
 *
 * Used by publishing transforms.
 *
 * @param actionData - The action properties data
 * @param classNameMap - Map of style block IDs to class names
 * @returns Transformed selectors array, or undefined if no transformation needed
 */
export function transformClassActionSelectors(
  actionData: Record<string, unknown>,
  classNameMap: ClassNameMap
): StyleBlockId[] | undefined {
  const dataClass = actionData.class as Record<string, unknown> | undefined;
  const selectors: StyleBlockId[] =
    (dataClass?.selectors as StyleBlockId[]) || [];
  const getNameById = getClassName(classNameMap);
  return selectors.length > 0
    ? selectors.flatMap((v) => getNameById(v) || [])
    : undefined;
}

/**
 * Gets CSS-safe class name from classNameMap.
 * Memoized for performance.
 */
const getClassName = weakMemo((classMap: ClassNameMap) => (guid: string) => {
  const styleBlockName = getNameFromClassNameMap(classMap, guid);
  return cssifyName(styleBlockName ? styleBlockName : '');
});

// ============================================================================
// Variable Transform Logic
// ============================================================================

/**
 * Formats a variable's typed value into a CSS string.
 *
 * @param value - A StyleVariables primitive, or a variable reference
 * @param resolveCssName - Optional resolver for variable references (needed for 'ref' type)
 * @returns The CSS-ready string representation, or empty string if unresolvable
 */
function formatVariableValue(
  value: NonNullable<VariableAction['value']>,
  resolveCssName?: (variableId: string) => string | undefined
): string {
  switch (value.type) {
    case 'color':
      return value.value;
    case 'length':
      return `${value.value.value}${value.value.unit}`;
    case 'number':
      return `${value.value.value}`;
    case 'percentage':
      return `${value.value.value}%`;
    case 'ref': {
      const refCssName = resolveCssName?.(value.value.variableId);
      return refCssName ? `var(${refCssName})` : '';
    }
  }
}

/**
 * Transforms variable action data from designer format to runtime format
 *
 * Converts an array of { variableId, value } objects into a flat map of
 * CSS variable names to formatted values (ex: { '--card-width': '250px' })
 *
 * This is used by both:
 * - Publishing transforms (getIx3Js)
 * - Stylesheet transforms (applyIX3Transforms)
 *
 * @param mutable_data - The action properties. Used in transform engine
 * @param resolveCssName - Function that resolves a variable ID to its CSS name
 */
export function transformVariableActionData(
  mutable_data: Record<string, unknown>,
  resolveCssName: (variableId: string) => string | undefined
): void {
  const variableConfig = mutable_data.variable as
    | {variables: VariableAction[]}
    | undefined;
  if (!variableConfig?.variables) return;

  const result: Record<string, string> = {};

  for (const entry of variableConfig.variables) {
    const cssName = resolveCssName(entry.variableId);
    if (!cssName) continue;
    if (entry.value === null) continue;
    const formatted = formatVariableValue(entry.value, resolveCssName);
    if (formatted) {
      result[cssName] = formatted;
    }
  }

  mutable_data.variable = result;
}

/**
 * Rewrites wf:custom-property keys from the names the panel stores to the names
 * GSAP reads.
 *
 * The panel stores the property name the author typed — `font-size` stays
 * `font-size` — so the field never rewrites their spelling in front of them.
 * That is not always a name GSAP knows, so this is where it becomes one:
 *
 *   "font-size"   -> "fontSize"
 *   "fontSize"    -> "fontSize"
 *   "--my-var"    -> "--my-var"
 *
 * Runs in every engine that feeds a GSAP timeline — designer preview, publish,
 * and library export — because a key that resolved differently between them
 * would animate one property on the canvas and another on the live site.
 *
 * Returns a replacement map rather than mutating in place: the engine assigns
 * whatever an action transform returns, and rebuilding is the only way to
 * change a key.
 *
 * @param properties - `action.properties['wf:custom-property']`
 */
export function transformCustomPropertyKeys(
  properties: Record<string, unknown>
): Record<string, unknown> {
  const rewritten: Record<string, unknown> = {};

  for (const key of Object.keys(properties)) {
    const next = canonicalizeCustomPropertyName(key);

    // The Designer is the only place the name allowlist runs, and the server
    // stores whatever names it is sent. GSAP sets a name that is not a style
    // property as an element property (`innerHTML`) or hands it to a plugin
    // (`attr`), so a name that gets past the Designer must not reach publish.
    if (!isCustomPropertyAllowed(next)) continue;

    // Two rows can only collide after canonicalization (`font-size` alongside
    // `fontSize`), which the panel prevents. Keep the first rather than let the
    // later row silently overwrite a value the author can still see.
    if (next in rewritten) continue;
    rewritten[next] = properties[key];
  }

  return rewritten;
}
